<template>
  <div :class="$style.gridContainer">
    <DynamicControlsCheckbox
      :class="$style.enableCheckboxContainer"
      :control="enableNotificationCheckboxConfigField"
      :handleValueChange="handleValueChange"
      :value="enableNotificationCheckboxValue"
    />
    <GridAuto
      v-show="enableNotificationCheckboxValue"
      minWidth="33rem"
    >
      <div>
        <DynamicControlsInput
          :class="$style.messageInputContainer"
          :control="notificationTextConfigField"
          :handleValueChange="handleValueChange"
          :value="notificationTextValue"
        />
        <div :class="$style.logoSectionContainer">
          <InputLabel text="Logo" />
          <div :class="$style.logoPreviewContainer">
            <component
              :is="logoComponent.component"
              v-bind="logoComponent.componentProps"
              :class="$style.logoImg"
            />
          </div>
          <ParagraphBody
            bodySize="tiny"
            :hasMargin="false"
          >
            If you haven't uploaded a logo for your organization, the JumpCloud logo is used by
            default. Go to
            <Link
              text="Settings"
              to="/settings/organization#logo"
            />
            to upload your logo.
          </ParagraphBody>
        </div>
      </div>
      <div>
        <SegmentedButton
          :class="$style.notificationPreviewToggle"
          label="Notification Preview"
          name="notificationPreviewToggle"
          :options="notificationOptions"
          :showValidationMessage="false"
          :value="notificationSelected"
          @input="handleNotificationChange"
        />
        <ParagraphBody
          id="notificationOSVersionNote"
          bodySize="tiny"
          :class="$style.notificationOSVersionNote"
          :hasMargin="false"
        >
          Customized notifications are only available for major upgrades on devices running
          macOS 12.3 and later
          <Link
            text="due to permission requirements"
            to="https://support.apple.com/en-us/HT213471"
          />. Users on devices running earlier versions will be notified of major upgrades
          when they are automatically scheduled for installation via MDM commands.
        </ParagraphBody>
        <div
          id="notificationImgContainer"
          :class="$style.notificationImgContainer"
        >
          <div
            :class="previewSectionClass"
          >
            <div
              :id="customDataContainerID"
              :class="customDataContainerClass"
            >
              <div
                :id="notificationPreviewLogoContainerID"
                :class="notificationPreviewLogoContainerClass"
              >
                <component
                  :is="logoComponent.component"
                  :id="logoNotificationPreviewID"
                  v-bind="logoComponent.componentProps"
                  :class="logoNotificationPreviewClass"
                  data-test-id="MacOspPolicyCustomNotificationSettings__logoImg"
                />
              </div>
              <div
                :id="notificationPreviewTextContainerID"
                :class="notificationPreviewTextContainerClass"
              >
                <p
                  :id="notificationTextID"
                  :class="notificationTextClass"
                  :style="notificationTextFontSize"
                >
                  {{ notificationTextValue }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </GridAuto>
  </div>
</template>

<script>

import {
  GridAuto, InputLabel, JumpcloudBrandmark, Link, ParagraphBody, SegmentedButton,
} from '@jumpcloud/ui-components';
import { mapState } from 'vuex';
import DynamicControlsCheckbox from 'client/components/DynamicControls/BodyComponents/DynamicControlsCheckbox.vue';
import DynamicControlsInput
  from 'client/components/DynamicControls/BodyComponents/DynamicControlsInput';
import OrganizationsApi from 'client/api/v2/OrganizationsApi';

const latestMacOsVersionsIndex = 0;

export default {
  name: 'MacOspPolicyCustomNotificationSettings',

  components: {
    DynamicControlsCheckbox,
    DynamicControlsInput,
    InputLabel,
    JumpcloudBrandmark,
    Link,
    ParagraphBody,
    SegmentedButton,
    GridAuto,
  },

  props: {
    configFields: {
      required: true,
      type: Array,
    },
    configValues: {
      required: true,
      type: Array,
    },
    handleValueChange: {
      required: true,
      type: Function,
    },
  },

  data: () => ({
    enableNotifications: false,
    notificationOptions: [
      {
        label: 'Latest Update',
        value: 'latestVersion',
      },
      {
        label: 'Minor Update',
        value: 'minorUpdate',
      },
    ],
    notificationSelected: 'latestVersion',
    logoUrl: '',
    notificationPreviewWidth: 0,
    resizeObserver: null,
  }),

  computed: {
    ...mapState('SettingsModule', {
      orgId: 'orgId',
    }),

    customDataContainerClass() {
      return this.isLatestVersionSelected ? this.$style.latestVersionCustomDataContainer
        : this.$style.majorMinorUpgradeCustomDataContainer;
    },

    customDataContainerID() {
      return this.isLatestVersionSelected ? 'latestVersionCustomDataContainer'
        : 'majorMinorUpgradeCustomDataContainer';
    },

    enableNotificationCheckboxConfigField() {
      return {
        ...this.configFields.find(({ name }) => name === 'EnableNotifications'),
      };
    },

    enableNotificationCheckboxValue() {
      const configValue = this.configValues.find(({ configFieldName }) => configFieldName
        === 'EnableNotifications');
      return configValue.value || false;
    },

    isLatestVersionSelected() {
      return this.notificationSelected === this.notificationOptions[latestMacOsVersionsIndex].value;
    },

    logoComponent() {
      const logo = {
        component: 'JumpcloudBrandmark',
        componentProps: { darkBg: false, scale: 3, type: 'jcLogo' },
      };

      if (this.orgHasLogo) {
        logo.component = 'img';
        logo.componentProps = { src: this.logoUrl };
      }

      return logo;
    },

    logoImgClass() {
      return this.orgHasLogo ? this.$style.jumpCloudLogoImg
        : this.$style.customOrgLogoImg;
    },

    logoNotificationPreviewClass() {
      if (this.isLatestVersionSelected) {
        return this.orgHasLogo ? this.$style.latestVersionLogoImg
          : this.$style.latestVersionJumpCloudLogoImg;
      }
      return this.$style.majorMinorUpgradeLogoImg;
    },

    logoNotificationPreviewID() {
      if (this.isLatestVersionSelected) {
        return this.orgHasLogo ? 'latestVersionLogoImg' : 'latestVersionJumpCloudLogoImg';
      }
      return 'majorMinorUpgradeLogoImg';
    },

    notificationPreviewLogoContainerClass() {
      return this.isLatestVersionSelected ? this.$style.latestVersionNotificationLogoContainer
        : this.$style.majorMinorUpgradeNotificationLogoContainer;
    },

    notificationPreviewLogoContainerID() {
      return this.isLatestVersionSelected ? 'latestVersionNotificationLogoContainer'
        : 'majorMinorUpgradeNotificationLogoContainer';
    },

    notificationPreviewTextContainerClass() {
      return this.isLatestVersionSelected ? this.$style.latestVersionNotificationTextContainer
        : this.$style.majorMinorUpgradeNotificationTextContainer;
    },

    notificationPreviewTextContainerID() {
      return this.isLatestVersionSelected ? 'latestVersionNotificationTextContainer'
        : 'majorMinorUpgradeNotificationTextContainer';
    },

    notificationTextClass() {
      return this.isLatestVersionSelected ? this.$style.latestVersionNotificationText
        : this.$style.majorMinorUpgradeNotificationText;
    },

    notificationTextConfigField() {
      return {
        ...this.configFields.find(({ name }) => name === 'NudgeMainHeaderText'),
        maxlength: 75,
        validators: 'max:75',
      };
    },

    notificationTextFontSize() {
      return { 'font-size': this.adjustPreviewFontSize() };
    },

    notificationTextID() {
      return this.isLatestVersionSelected ? 'latestVersionNotificationText'
        : 'majorMinorUpgradeNotificationText';
    },

    notificationTextValue() {
      const configValue = this.configValues.find(({ configFieldName }) => configFieldName
        === 'NudgeMainHeaderText');
      return configValue.value || '';
    },

    orgHasLogo() {
      return !!this.logoUrl;
    },

    previewSectionClass() {
      switch (this.notificationSelected) {
        case 'latestVersion':
          return this.$style.latestVersionImg;
        case 'minorUpdate':
        default:
          return this.$style.minorUpgradeImg;
      }
    },
  },

  mounted() {
    this.handleMounted();
    this.resizeObserver = new ResizeObserver(this.adjustNotificationPreviewLayout);
    this.resizeObserver.observe(document.getElementById('notificationImgContainer'));
  },
  beforeDestroy() {
    this.resizeObserver.disconnect();
  },

  methods: {
    adjustNotificationPreviewLayout() {
      // Get current width of notification preview image container in rem
      this.notificationPreviewWidth = document.getElementById('notificationImgContainer').offsetWidth
      / parseFloat(getComputedStyle(document.documentElement).fontSize);

      // Adjust position and size of logo and text, for latest preview,
      // based on image container size
      if (document.getElementById('latestVersionCustomDataContainer')) {
        if (document.getElementById('latestVersionJumpCloudLogoImg')) {
          document.getElementById('latestVersionCustomDataContainer').style.marginTop = `${this.notificationPreviewWidth / 7.969}rem`;
          document.getElementById('latestVersionJumpCloudLogoImg').style.maxWidth = '';
        } else {
          document.getElementById('latestVersionCustomDataContainer').style.marginTop = `${this.notificationPreviewWidth / 15.682}rem`;
        }
        document.getElementById('latestVersionCustomDataContainer').style.height = '';
      }
      if (document.getElementById('latestVersionLogoImg')) {
        document.getElementById('latestVersionLogoImg').style.maxHeight = `${this.notificationPreviewWidth / 5.448}rem`;
        document.getElementById('latestVersionLogoImg').style.maxWidth = '';
      }
      if (document.getElementById('latestVersionNotificationLogoContainer')) {
        document.getElementById('latestVersionNotificationLogoContainer').style.width = '';
      }
      if (document.getElementById('latestVersionNotificationTextContainer')) {
        document.getElementById('latestVersionNotificationTextContainer').style.maxWidth = '';
      }
      if (document.getElementById('latestVersionNotificationText')) {
        document.getElementById('latestVersionNotificationText').style.marginLeft = '';
        document.getElementById('latestVersionNotificationText').style.marginTop = '';
      }

      // Adjust position and size of logo and text, for major and minor preview,
      // based on image container size
      if (document.getElementById('majorMinorUpgradeNotificationLogoContainer')) {
        document.getElementById('majorMinorUpgradeNotificationLogoContainer').style.width = `${this.notificationPreviewWidth / 2.6422}rem`;
      }
      if (document.getElementById('majorMinorUpgradeCustomDataContainer')) {
        document.getElementById('majorMinorUpgradeCustomDataContainer').style.height = `${this.notificationPreviewWidth / 3.5111}rem`;
        document.getElementById('majorMinorUpgradeCustomDataContainer').style.marginTop = '';
      }
      if (document.getElementById('majorMinorUpgradeLogoImg')) {
        document.getElementById('majorMinorUpgradeLogoImg').style.maxHeight = '50%';
        document.getElementById('majorMinorUpgradeLogoImg').style.maxWidth = '50%';
      }
      if (document.getElementById('majorMinorUpgradeNotificationText')) {
        const notificationTextElement = document.getElementById('majorMinorUpgradeNotificationText');
        notificationTextElement.style.marginLeft = `${this.notificationPreviewWidth / 97.23}rem`;
        notificationTextElement.style.fontSize = this.adjustPreviewFontSize();
        if (this.hasTextWrapped(notificationTextElement.firstChild)) {
          notificationTextElement.style.marginTop = `${this.notificationPreviewWidth / 16.76}rem`;
        } else {
          notificationTextElement.style.marginTop = `${this.notificationPreviewWidth / 13.89}rem`;
        }
      }
      if (document.getElementById('majorMinorUpgradeNotificationTextContainer')) {
        document.getElementById('majorMinorUpgradeNotificationTextContainer').style.maxWidth = `${this.notificationPreviewWidth / 2.019}rem`;
      }
    },

    adjustPreviewFontSize() {
      // Adjust font size for notification preview text based on image container size
      // Formula:
      // (min_font_size + (max_font_size - min_font_size) * ((container_width - min_container_width)
      // / (max_container_width - min_container_width)))
      if (this.notificationTextValue.length > 55) {
        return `${(0.5 + (0.8 - 0.5) * ((this.notificationPreviewWidth - 33) / 15.6154))}rem`;
      }
      if (this.notificationTextValue.length > 45) {
        return `${(0.6 + (0.9 - 0.6) * ((this.notificationPreviewWidth - 33) / 15.6154))}rem`;
      }

      return `${(0.7 + (1.0 - 0.7) * ((this.notificationPreviewWidth - 33) / 15.6154))}rem`;
    },

    async getOrgLogo() {
      this.logoUrl = await OrganizationsApi.getOrganizationLogoUrl(this.orgId);
    },

    handleMounted() {
      this.getOrgLogo();
      this.showHideNotificationOSVersionNote();
    },

    handleNotificationChange(value) {
      this.notificationSelected = value;
      setTimeout(() => {
        this.observeNotificationPreviewText();
        this.adjustNotificationPreviewLayout();
        this.showHideNotificationOSVersionNote();
      }, 1);
    },

    hasTextWrapped(textNode) {
      if (textNode.nodeType !== 3) {
        return false;
      }
      const notificationTextNode = textNode;
      notificationTextNode.textContent = textNode.textContent.trim().replace(/\s+/g, ' ');
      const range = document.createRange();
      range.setStart(notificationTextNode, 0);
      range.setEnd(notificationTextNode, notificationTextNode.textContent.length);
      if (range.getClientRects().length > 1) {
        return true;
      }
      return false;
    },

    observeNotificationPreviewText() {
      if (document.getElementById('majorMinorUpgradeNotificationText')) {
        this.resizeObserver.observe(document.getElementById('majorMinorUpgradeNotificationText'));
      }
    },

    showHideNotificationOSVersionNote() {
      if (document.getElementById('notificationOSVersionNote')) {
        if (this.notificationSelected === 'majorUpgrade') {
          document.getElementById('notificationOSVersionNote').style.display = '';
        } else {
          document.getElementById('notificationOSVersionNote').style.display = 'none';
        }
      }
    },
  },
};
</script>

<style module scoped>

.enableCheckboxContainer {
  margin-bottom: 20px;
}

.gridContainer {
  max-width: 1280px;
}

.latestVersionCustomDataContainer {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-height: 12rem;
}

.latestVersionImg {
  background:
    url('https://s3.amazonaws.com/static.jumpcloud.com/images/mac-osp-policy-latest-versions-notification.png')
    no-repeat;
  background-size: contain;
  flex-grow: 1;
}

.latestVersionJumpCloudLogoImg {
  font-size: 3em;
  margin-bottom: 2rem;
  max-width: 100%;
  text-align: center;
}

.latestVersionNotificationLogoContainer {
  align-items: center;
  display: flex;
  justify-content: center;
}

.latestVersionNotificationText {
  font-weight: 800;
}

.logoImg {
  max-height: 10rem;
  max-width: 10rem;
  text-align: center;
}

.logoPreviewContainer {
  align-items: center;
  background-color: var(--jc-color-background);
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
}

.majorMinorUpgradeCustomDataContainer {
  display: flex;
  max-height: 14.3rem;
}

.majorMinorUpgradeNotificationLogoContainer {
  align-items: center;
  display: flex;
  justify-content: center;
  max-width: 19.2rem;
}

.majorMinorUpgradeNotificationText {
  font-weight: 800;
  margin-left: 1.4rem;
  margin-top: 1rem;
}

.majorMinorUpgradeNotificationTextContainer {
  max-width: 24rem;
}

.majorUpgradeImg {
  background:
    url('https://s3.amazonaws.com/static.jumpcloud.com/images/mac-osp-policy-major-upgrade.png')
    no-repeat;
  background-size: contain;
  flex-grow: 1;
}

.messageInputContainer {
  margin: 0;
}

.minorUpgradeImg {
  background:
    url('https://s3.amazonaws.com/static.jumpcloud.com/images/mac-osp-policy-minor-update-ventura.png')
    no-repeat;
  background-size: contain;
  flex-grow: 1;
}

.notificationImgContainer {
  display: flex;
  margin-top: 1rem;
  max-width: 48.6rem;
  min-height: 28rem;
}

.notificationOSVersionNote {
  margin-top: 0.5rem;
}

.notificationPreviewToggle /deep/ div {
  max-width: 36rem;
}
</style>
